import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AppShell } from '@/components/layout/AppShell';
import { BookCarousel } from '@/components/books/BookCarousel';
import { BookDetailModal } from '@/components/books/BookDetailModal';
import { EditorialCollectionCard } from '@/components/books/EditorialCollectionCard';
import { LoadingGrid } from '@/components/books/LoadingGrid';
import { AiSearchHero } from '@/components/search/AiSearchHero';
import { OllieLearnsPromoBanner } from '@/components/learn/OllieLearnsPromoBanner';
import { booksService } from '@/services/books.service';
import { searchService } from '@/services/search.service';
import { labelsService } from '@/services/labels.service';
import { ApiError } from '@/services/http';
import { useReadersStore } from '@/store/readers.store';
import { useGuestStore } from '@/store/guest.store';
import { useIsGuest } from '@/hooks/useIsGuest';
import { useGuestAuthActions } from '@/hooks/useGuestAuthActions';
import { GuestWelcomeCard } from '@/components/guest/GuestWelcomeCard';
import { GuestReadingLevelSelector } from '@/components/guest/GuestReadingLevelSelector';
import { CAROUSEL_CONFIGS, type CarouselConfig } from '@/constants/carousel-configs';
import { cn } from '@/lib/cn';
import type { BookView, LabelView, SearchHitView } from '@/types/api';

/**
 * Library — `/library`. The default landing page after login.
 *
 * Phase 4 scope (full):
 *   - `AiSearchHero` calls `POST /search` and renders results in a sync
 *     `BookCarousel` above the personalized shelf. A `LoadingGrid`
 *     stands in while the embedding query is in flight.
 *   - A personalized shelf for the selected reader, driven by the
 *     reader's interest / reading_level labels.
 *   - Lazy-loaded explore carousels from `CAROUSEL_CONFIGS`; reveal one
 *     at a time as the user scrolls.
 *   - `EditorialCollectionCard` — a curator-authored promo tile pointing
 *     at a deep link into the catalogue. Books are seeded from an
 *     existing carousel config so every account sees real covers.
 *
 * Learn With Ollie is wired inside `BookDetailModal` — see that component.
 */
export function LibraryPage() {
  const navigate = useNavigate();
  const selectedReader = useReadersStore((s) => s.selectedReader());
  const [selectedBook, setSelectedBook] = useState<BookView | null>(null);

  // --- Guest mode ---------------------------------------------------------
  // Unauthenticated visitors browse with no reader/account. Shelves are
  // scoped by the reading levels they pick in the intro overlay (persisted
  // in the guest store). Search / personalized shelf / Learns promo are
  // hidden — they need a reader/account.
  const isGuest = useIsGuest();
  const guestLevels = useGuestStore((s) => s.readingLevels);
  const guestHasChosen = useGuestStore((s) => s.hasChosenLevels);
  const chooseGuestLevels = useGuestStore((s) => s.chooseReadingLevels);
  const { signIn } = useGuestAuthActions();

  const guestPreChoice = isGuest && !guestHasChosen;
  const [introStep, setIntroStep] = useState<'welcome' | 'levels'>('welcome');
  const [editingLevels, setEditingLevels] = useState(false);
  const showIntroOverlay = guestPreChoice || editingLevels;

  // Reading-level options for the guest selector — loaded on demand the
  // first time the overlay is shown (public GET /labels?labelType=reading_level).
  const [levelOptions, setLevelOptions] = useState<LabelView[]>([]);
  const [levelOptionsLoading, setLevelOptionsLoading] = useState(false);
  const [levelOptionsError, setLevelOptionsError] = useState<string | null>(null);
  // Single-flight latch (ref, not state) so toggling the loading flag — and
  // React 18 StrictMode's double-mount — can't re-run this effect, cancel the
  // in-flight fetch, and drop the result. Mirrors the bootstrap latch in
  // ProtectedRoute.
  const levelsRequestedRef = useRef(false);
  const loadLevels = useCallback(async () => {
    levelsRequestedRef.current = true;
    setLevelOptionsLoading(true);
    setLevelOptionsError(null);
    try {
      const opts = await labelsService.list('reading_level');
      setLevelOptions(opts);
    } catch (err) {
      // Surface instead of swallowing — a silent catch made a failed fetch
      // look like "no reading levels". Logged + shown with a retry.
      console.warn('[guest] failed to load reading levels', err);
      setLevelOptionsError(
        err instanceof ApiError ? err.message : 'Could not load reading levels.',
      );
      levelsRequestedRef.current = false; // allow retry
    } finally {
      setLevelOptionsLoading(false);
    }
  }, []);
  useEffect(() => {
    if (!showIntroOverlay || levelsRequestedRef.current) return;
    void loadLevels();
  }, [showIntroOverlay, loadLevels]);

  // --- AI search state ---
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<SearchHitView[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const runSearch = async (query: string) => {
    setSearchQuery(query);
    setSearchLoading(true);
    setSearchResults([]);
    try {
      const result = await searchService.query({
        query,
        limit: 12,
        // Scope search to the selected reader's reading levels, same as the
        // shelves. Undefined when no reader / no levels → catalogue-wide.
        readingLevels: selectedReader?.readingLevels.map((l) => l.labelValue),
      });
      setSearchResults(result.books);
      if (result.books.length === 0) {
        toast.info(`No matches for “${query}”. Try a different angle.`);
      }
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : 'Search ran into trouble. Please try again.';
      toast.error(message);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery(null);
    setSearchResults([]);
    setSearchLoading(false);
  };

  // Switching readers drops any active search: the displayed results were
  // scoped to the previously-selected reader, so a fresh search should re-run
  // in the new reader's context. Keyed on the reader id, so it fires on every
  // switch (and harmlessly on first mount, when the search is already empty).
  useEffect(() => {
    setSearchQuery(null);
    setSearchResults([]);
    setSearchLoading(false);
  }, [selectedReader?.id]);

  // --- Personalized shelf: random books that match the reader's interests ---
  const [shelfBooks, setShelfBooks] = useState<BookView[]>([]);
  const [shelfLoading, setShelfLoading] = useState(false);

  useEffect(() => {
    if (!selectedReader) {
      setShelfBooks([]);
      return;
    }
    // Only ask the server if the reader actually has interests on file;
    // otherwise the RPC would return the entire catalogue and we'd rather
    // render the "pick some interests" hint.
    if (selectedReader.interests.length === 0 && selectedReader.readingLevels.length === 0) {
      setShelfBooks([]);
      return;
    }

    let cancelled = false;
    setShelfLoading(true);

    const interestAliases = selectedReader.interests.map((l) => l.labelValue);
    const readingLevelAliases = selectedReader.readingLevels.map((l) => l.labelValue);

    (async () => {
      try {
        // Reading level is enforced as a hard AND constraint via the new
        // `required_reading_level` param so the personalized shelf only
        // surfaces books at the reader's level *and* matching at least one
        // of their interests — not the loose OR union the old endpoint did.
        //
        // Over-fetch a randomised pool so we can partition client-side into
        // two tiers without a second round-trip:
        //   * Tier 1 (first 20): books that ALSO have a live quiz AND a
        //     book activity AND a vocabulary-bookmark image. These are the
        //     "fully-equipped" picks we want to feature prominently.
        //   * Tier 2 (next 20): the rest of the matches — interest/level
        //     alignment is enough; extensions optional.
        // Pool size = 80 gives the partition room before tier 1 runs out.
        // If admins haven't populated extensions for enough matches, tier 1
        // will be shorter than 20; that's intentional — we'd rather render
        // a smaller "featured" group than pad it with un-equipped books.
        const result = await booksService.list({
          genre: interestAliases,
          topic: interestAliases,
          interest_level: interestAliases,
          required_reading_level:
            readingLevelAliases.length > 0 ? readingLevelAliases : undefined,
          limit: 80,
          randomize: true,
        });
        const fullyEquipped: BookView[] = [];
        const remaining: BookView[] = [];
        for (const book of result.books) {
          if (book.hasQuiz && book.hasResources && !!book.vocabularyBookmark) {
            fullyEquipped.push(book);
          } else {
            remaining.push(book);
          }
        }
        const featured = fullyEquipped.slice(0, 20);
        const filler = remaining.slice(0, 20);
        if (!cancelled) setShelfBooks([...featured, ...filler]);
      } catch (err) {
        if (cancelled) return;
        toast.error(err instanceof ApiError ? err.message : 'Could not load the shelf.');
        setShelfBooks([]);
      } finally {
        if (!cancelled) setShelfLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedReader]);

  // Reader-scoped reading-level alias list, used as the `required_reading_level`
  // INTERSECT filter on every Library fetch so each carousel stays inside the
  // selected reader's reading level. `undefined` (rather than `[]`) when the
  // reader has none on file — the server treats an empty array as "no
  // constraint" and we want to mirror that explicitly.
  const readerReadingLevels = useMemo(() => {
    // Guests scope by their picked levels (empty = browse all = no constraint).
    if (isGuest) {
      return guestLevels.length > 0 ? guestLevels.map((l) => l.alias) : undefined;
    }
    if (!selectedReader || selectedReader.readingLevels.length === 0) return undefined;
    return selectedReader.readingLevels.map((l) => l.labelValue);
    // selectedReader is referentially stable per reader switch, so this
    // memo only recomputes when the active reader changes.
  }, [isGuest, guestLevels, selectedReader]);

  // Identity for the explore carousels' `key`, so changing reader OR guest
  // levels remounts each strip and re-fetches at the new scope.
  const shelfScopeKey = isGuest
    ? `guest:${guestLevels.map((l) => l.alias).sort().join(',')}`
    : (selectedReader?.id ?? 'none');

  // --- Editorial collection: stable config + its own fetch. ---
  // `EDITORIAL_COLLECTION` pins the kicker/copy so a future CMS surface
  // can feed it without touching the Library. The book fetch is a plain
  // `GET /books?genre=…` call; four random covers is the ceiling per
  // the design spec.
  const [editorialBooks, setEditorialBooks] = useState<BookView[]>([]);

  useEffect(() => {
    // Skip while the guest is still on the intro overlay (the editorial card
    // is hidden behind it anyway — no point firing the fetch).
    if (guestPreChoice) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await booksService.list({
          genre: [...EDITORIAL_COLLECTION.genres],
          required_reading_level: readerReadingLevels,
          limit: 4,
          randomize: true,
        });
        if (!cancelled) setEditorialBooks(result.books);
      } catch (err) {
        if (cancelled) return;
        console.warn('Editorial collection fetch failed:', err);
        setEditorialBooks([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [readerReadingLevels, guestPreChoice]);

  // --- Explore carousels: randomize order, reveal as the user scrolls ---
  const shuffledConfigs = useMemo(() => shuffle(CAROUSEL_CONFIGS), []);
  const [revealedCount, setRevealedCount] = useState(1);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (revealedCount >= shuffledConfigs.length) return;
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setRevealedCount((prev) => Math.min(prev + 1, shuffledConfigs.length));
        }
      },
      { rootMargin: '400px 0px', threshold: 0 },
    );
    observer.observe(node);
    return () => observer.disconnect();
    // `guestPreChoice` is a dep so this re-runs when the guest confirms their
    // levels: the sentinel only renders once the real shelves replace the
    // skeleton, and without re-running here the observer would never attach to
    // it — leaving only the first row revealed until a manual refresh.
  }, [revealedCount, shuffledConfigs.length, guestPreChoice]);

  const visibleConfigs = shuffledConfigs.slice(0, revealedCount);
  const allRevealed = revealedCount >= shuffledConfigs.length;
  const hasSearch = searchQuery !== null;

  return (
    <AppShell mode="kid">
      {/* Guest level bar — lets a guest see + change the levels their shelves
          are scoped to (only once a choice has been made). */}
      {isGuest && guestHasChosen ? (
        <div className="mb-6 flex flex-wrap items-center gap-2 rounded-2xl border border-or-slate-200 bg-white px-4 py-3 text-[13px]">
          <span className="font-semibold text-or-slate-700">Showing books for:</span>
          {guestLevels.length > 0 ? (
            <span className="flex flex-wrap gap-1.5">
              {guestLevels.map((l) => (
                <span
                  key={l.id}
                  className="rounded-full bg-or-orange-50 px-2.5 py-0.5 text-[12px] font-semibold text-or-orange-700"
                >
                  {l.alias}
                </span>
              ))}
            </span>
          ) : (
            <span className="text-or-slate-600">All reading levels</span>
          )}
          <button
            type="button"
            onClick={() => setEditingLevels(true)}
            className="ml-auto text-[12px] font-semibold text-or-orange-600 hover:text-or-orange-700"
          >
            Change
          </button>
        </div>
      ) : null}

      <div
        className={cn(
          'flex flex-col gap-10',
          guestPreChoice && 'pointer-events-none select-none blur-sm',
        )}
      >
        {/* Search is hidden for guests (they have no reader to scope it to). */}
        {!isGuest ? (
          <AiSearchHero
            readerName={selectedReader?.name ?? null}
            onSearch={runSearch}
            onClear={clearSearch}
            loading={searchLoading}
            activeQuery={searchQuery}
          />
        ) : null}

        {/* --- Search results (only after a search has been run) ---
            Sits directly under the AI search hero so the user's matches
            are the first thing they see after submitting a query, before
            any promotional content. */}
        {hasSearch ? (
          <section className="flex flex-col gap-3">
            <div>
              <div className="or-kicker">Search results</div>
              <h2 className="font-display text-[20px] font-bold text-or-slate-900">
                This is what we found for “{searchQuery}” (and related topics)
              </h2>
            </div>
            {searchLoading ? (
              <LoadingGrid count={4} />
            ) : searchResults.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-or-slate-200 bg-white/60 px-6 py-10 text-center text-[13px] text-or-slate-500">
                No matches yet — try another phrase or pick from the suggestions.
              </div>
            ) : (
              <BookCarousel mode="sync" books={searchResults} onSelectBook={setSelectedBook} />
            )}
          </section>
        ) : null}

        {/* --- Ollie Learns discovery banner ---
            Wrapper pulls up from the parent's `gap-10` (40px) so the
            banner sits 28px below the section above per spec. Hidden for
            guests — Ollie Learns is account-gated. */}
        {!isGuest ? (
          <div className="hidden -mt-3 sm:block">
            <OllieLearnsPromoBanner />
          </div>
        ) : null}

        {/* --- Personalized shelf --- */}
        {selectedReader ? (
          <section className="flex flex-col gap-4">
            <BookCarousel
              mode="sync"
              title={`For ${selectedReader.name}`}
              kicker="Personalized shelf"
              books={shelfBooks}
              loading={shelfLoading}
              onSelectBook={setSelectedBook}
              emptyMessage="No books to show yet — Add Reader Interests to find matches."
              action={
                selectedReader.interests.length === 0 ? (
                  <Link
                    to={`/readers/${selectedReader.id}`}
                    className="text-[13px] font-semibold text-or-orange-600 hover:text-or-orange-700"
                  >
                    Pick interests →
                  </Link>
                ) : undefined
              }
            />
          </section>
        ) : null}

        {/* --- Explore carousels (lazy reveal) ---
            While a guest is still on the intro overlay we render static
            skeletons behind the blur (no network); once levels are picked
            the real strips fetch. */}
        {guestPreChoice ? (
          <GuestLibrarySkeleton />
        ) : (
          <>
            {/* The scope key (reader id, or the guest's level set) is part of
                each carousel's key so switching reader / guest levels remounts
                the strip and re-fetches. Async BookCarousel only fetches on
                mount, so without the remount the strips would keep the
                previous scope's books. */}
            {visibleConfigs.map((config) => (
              <BookCarousel
                key={`${config.id}:${shelfScopeKey}`}
                mode="async"
                title={config.title}
                filters={carouselFilters(config, readerReadingLevels)}
                onSelectBook={setSelectedBook}
              />
            ))}

            {/* Editorial card inserts itself once the user has revealed a few
                rows so it feels like editorial punctuation rather than an ad. */}
            {revealedCount >= Math.min(3, shuffledConfigs.length) ? (
              <EditorialCollectionCard
                kicker={EDITORIAL_COLLECTION.kicker}
                title={EDITORIAL_COLLECTION.title}
                description={EDITORIAL_COLLECTION.description}
                curatedBy={EDITORIAL_COLLECTION.curatedBy}
                ctaLabel="Open collection"
                onOpen={() => navigate(EDITORIAL_COLLECTION.ctaHref)}
                books={editorialBooks}
              />
            ) : null}

            {/* Sentinel for the lazy-loader. Kept after the rendered carousels
                so that scrolling past the last visible strip pulls the next. */}
            {!allRevealed ? <div ref={sentinelRef} aria-hidden className="h-px" /> : null}

            {allRevealed ? (
              <div className="mt-4 flex justify-center">
                <Link
                  to="/catalog"
                  className="inline-flex items-center gap-1 rounded-full border border-or-slate-200 bg-white px-5 py-2 text-[13px] font-semibold text-or-orange-600 shadow-or-card transition hover:bg-or-orange-50"
                >
                  Explore even more →
                </Link>
              </div>
            ) : null}
          </>
        )}
      </div>

      {/* Guest intro overlay: Welcome card → reading-level selector, over the
          blurred library. `editingLevels` re-opens just the selector. */}
      {showIntroOverlay ? (
        <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-or-slate-900/30 px-4 py-10 sm:items-center">
          <div className="w-full max-w-md">
            {guestPreChoice && introStep === 'welcome' ? (
              <GuestWelcomeCard onContinue={() => setIntroStep('levels')} />
            ) : (
              <GuestReadingLevelSelector
                levels={levelOptions}
                loading={levelOptionsLoading}
                error={levelOptionsError}
                onRetry={loadLevels}
                initialSelectedIds={guestLevels.map((l) => l.id)}
                onConfirm={(picked) => {
                  chooseGuestLevels(picked);
                  setEditingLevels(false);
                  setIntroStep('welcome');
                }}
                onSignIn={signIn}
                onClose={editingLevels && !guestPreChoice ? () => setEditingLevels(false) : undefined}
              />
            )}
          </div>
        </div>
      ) : null}

      <BookDetailModal
        book={selectedBook}
        onOpenChange={(open) => {
          if (!open) setSelectedBook(null);
        }}
        onSelectBook={(next) => setSelectedBook(next)}
      />
    </AppShell>
  );
}

/** Static skeleton shelves shown blurred behind the guest intro overlay. */
function GuestLibrarySkeleton() {
  return (
    <div className="flex flex-col gap-10" aria-hidden>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex flex-col gap-3">
          <div className="h-5 w-48 rounded-full bg-or-slate-100" />
          <LoadingGrid count={5} />
        </div>
      ))}
    </div>
  );
}

/**
 * Editorial curator copy. Kept as a module-level constant so the card is
 * stable across renders; a future CMS integration can drop this in. The
 * genre list seeds the right-column cover fan and the CTA deep link so
 * the whole card stays coherent.
 */
const EDITORIAL_COLLECTION = {
  kicker: 'Staff Pick Collection',
  title: 'Brave kids, bold quests',
  description:
    'Stories about curious, daring heroines solving mysteries, exploring new worlds, and proving what small hands can do.',
  curatedBy: 'Curated by 27 votes',
  genres: ['Adventure', 'Fantasy', 'Mystery'] as const,
  ctaHref: '/catalog?genre=Adventure&genre=Fantasy&genre=Mystery',
} as const;

/**
 * Converts a CarouselConfig's labelType + values into the filter payload.
 * `readerReadingLevels` is appended as the `required_reading_level` AND
 * constraint so every Library carousel stays scoped to the selected
 * reader's reading level. Reading-level carousels skip the AND constraint
 * because the carousel's own label type is already the reading level —
 * imposing the reader's level on top would empty out any strip outside
 * their band.
 */
function carouselFilters(
  config: CarouselConfig,
  readerReadingLevels: string[] | undefined,
) {
  const required =
    config.labelType === 'reading_level' ? undefined : readerReadingLevels;
  switch (config.labelType) {
    case 'genre':
      return { genre: config.values, required_reading_level: required };
    case 'topic':
      return { topic: config.values, required_reading_level: required };
    case 'reading_level':
      return { reading_level: config.values };
    case 'interest_level':
      return { interest_level: config.values, required_reading_level: required };
    case 'historical_event':
      return { historical_event: config.values, required_reading_level: required };
    case 'time_period':
      return { time_period: config.values, required_reading_level: required };
    case 'book_series':
      return { book_series: config.values, required_reading_level: required };
  }
}

/** Fisher-Yates. Returns a fresh array so callers can memoize safely. */
function shuffle<T>(input: readonly T[]): T[] {
  const out = input.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}
